import type { DungeonMonsterAllowance, DungeonShopConfig } from "@/dungeon/dungeon-shop-types";
import { resolveMonsterShopEntry } from "@/dungeon/dungeon-shop-types";
import { goldBoughtCount, vaultStock } from "@/dungeon/dungeon-monster-vault";
import type { HomeEconomy } from "@/home/economy";
import type { TowerFloorSpawnRow } from "@/tower/tower-floor-config";

export type PurchaseFail = { ok: false; reason: string };
export type PurchaseOk<T> = { ok: true } & T;

function spendCoins(economy: HomeEconomy, amount: number): HomeEconomy | null {
  if (amount <= 0) return economy;
  if (economy.wallet.coins < amount) return null;
  return {
    ...economy,
    wallet: { ...economy.wallet, coins: economy.wallet.coins - amount },
  };
}

function spendCash(economy: HomeEconomy, amount: number): HomeEconomy | null {
  if (amount <= 0) return economy;
  if (economy.wallet.cash < amount) return null;
  return {
    ...economy,
    wallet: { ...economy.wallet, cash: economy.wallet.cash - amount },
  };
}

export function spawnCountForMonster(
  spawns: TowerFloorSpawnRow[],
  monsterId: string,
): number {
  return spawns
    .filter((s) => s.monsterId === monsterId)
    .reduce((sum, s) => sum + s.count, 0);
}

/** @deprecated Per-floor allowance — use buyDungeonMonsterForVault */
export function buyDungeonMonsterUnit(
  economy: HomeEconomy,
  allowance: DungeonMonsterAllowance,
  shop: DungeonShopConfig,
  monsterId: string,
  currency: "gold" | "cash",
): PurchaseOk<{ economy: HomeEconomy; allowance: DungeonMonsterAllowance }> | PurchaseFail {
  const res = buyDungeonMonsterForVault(economy, shop, monsterId, currency);
  if (!res.ok) return res;
  const slot = allowance[monsterId] ?? { gold: 0, cash: 0 };
  return {
    ok: true,
    economy: res.economy,
    allowance: {
      ...allowance,
      [monsterId]: {
        ...slot,
        gold: currency === "gold" ? slot.gold + 1 : slot.gold,
        cash: currency === "cash" ? slot.cash + 1 : slot.cash,
      },
    },
  };
}

export function buyDungeonMonsterForVault(
  economy: HomeEconomy,
  shop: DungeonShopConfig,
  monsterId: string,
  currency: "gold" | "cash",
): PurchaseOk<{ economy: HomeEconomy }> | PurchaseFail {
  const entry = resolveMonsterShopEntry(shop, monsterId);

  if (currency === "gold") {
    const bought = goldBoughtCount(economy, monsterId);
    if (bought >= entry.goldMaxUnits) {
      return { ok: false, reason: `ซื้อด้วยทองครบแล้ว (สูงสุด ${entry.goldMaxUnits})` };
    }
    const nextEconomy = spendCoins(economy, entry.goldPricePerUnit);
    if (!nextEconomy) {
      return { ok: false, reason: `ทองไม่พอ · ต้องการ ${entry.goldPricePerUnit}` };
    }
    const vault = { ...nextEconomy.dungeonMonsterVault };
    vault[monsterId] = vaultStock(nextEconomy, monsterId) + 1;
    const goldBought = { ...nextEconomy.dungeonMonsterGoldBought };
    goldBought[monsterId] = bought + 1;
    return {
      ok: true,
      economy: {
        ...nextEconomy,
        dungeonMonsterVault: vault,
        dungeonMonsterGoldBought: goldBought,
      },
    };
  }

  const nextEconomy = spendCash(economy, entry.cashPricePerUnit);
  if (!nextEconomy) {
    return { ok: false, reason: `Cash ไม่พอ · ต้องการ ${entry.cashPricePerUnit}` };
  }
  const vault = { ...nextEconomy.dungeonMonsterVault };
  vault[monsterId] = vaultStock(nextEconomy, monsterId) + 1;
  return {
    ok: true,
    economy: { ...nextEconomy, dungeonMonsterVault: vault },
  };
}

export function buyDungeonPowerStep(
  economy: HomeEconomy,
  shop: DungeonShopConfig,
  currentPower: number,
  currency: "gold" | "cash",
): PurchaseOk<{ economy: HomeEconomy; powerMultiplier: number }> | PurchaseFail {
  const step = shop.powerStep;
  const next = Math.round((currentPower + step) * 100) / 100;

  if (currency === "gold") {
    if (currentPower >= shop.goldMaxPowerMultiplier) {
      return {
        ok: false,
        reason: `คูณพลังด้วยทองสูงสุด ×${shop.goldMaxPowerMultiplier} — ใช้ Cash เพื่อเพิ่มต่อ`,
      };
    }
    if (next > shop.goldMaxPowerMultiplier) {
      return {
        ok: false,
        reason: `เกินเพดานทอง ×${shop.goldMaxPowerMultiplier} — ลอง Cash`,
      };
    }
    const nextEconomy = spendCoins(economy, shop.powerGoldPricePerStep);
    if (!nextEconomy) {
      return { ok: false, reason: `ทองไม่พอ · ต้องการ ${shop.powerGoldPricePerStep}` };
    }
    return { ok: true, economy: nextEconomy, powerMultiplier: next };
  }

  if (currentPower >= shop.cashMaxPowerMultiplier) {
    return { ok: false, reason: `คูณพลังสูงสุด ×${shop.cashMaxPowerMultiplier}` };
  }
  if (next > shop.cashMaxPowerMultiplier) {
    return { ok: false, reason: `เกินเพดาน ×${shop.cashMaxPowerMultiplier}` };
  }
  const nextEconomy = spendCash(economy, shop.powerCashPricePerStep);
  if (!nextEconomy) {
    return { ok: false, reason: `Cash ไม่พอ · ต้องการ ${shop.powerCashPricePerStep}` };
  }
  return { ok: true, economy: nextEconomy, powerMultiplier: next };
}
